import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import { ProjectFilteringSlice } from "@/store/project-filtering/project-filtering-slice";
import { UNTAGGED } from "@/store/tags/tags-slice";
import { GUID } from "@faro-lotv/foundation";
import {
  GENERIC_DATASET_TYPE_HINTS,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import {
  ClustersRecord,
  IElementsRecord,
  SINGLE_SCAN_TYPE_HINTS,
} from "@faro-lotv/project-source";
import { DataSetAreaInfo, LightweightIElement } from "@faro-lotv/service-wires";

/** Callback that should return true if the element matches the filtering criteria */
export type FilterFunction = (element: LightweightIElement) => boolean;

/**
 * @returns True if the input element fits the filtering parameters.
 *   Meaning that the element is considered to be filtered in, or pass the filters.
 * @param element The element to check
 * @param iElementsRecord The iElements in the store
 * @param filters The filters parameters
 */
export function isIElementFiltered(
  element: IElement | LightweightIElement,
  iElementsRecord: IElementsRecord | LightweightElementsRecord,
  filters: FilterFunction[],
): boolean {
  if (filters.length === 0) return false;

  const passesAllFilters = elementOrDescendantsPassFilters(
    element.id,
    iElementsRecord,
    filters,
  );

  return !passesAllFilters;
}

/**
 * @returns The list of callbacks that should be used given the input filters parameters
 * @param filters The filter parameters
 */
export function computeFilterFunctions(
  filters: ProjectFilteringSlice | undefined,
): FilterFunction[] {
  if (!filters) return [];
  const filterFunctions = [];
  const { start, end } = filters.dateRange;
  if (start || end) {
    const startDate = start ? new Date(start) : undefined;
    const endDate = end ? new Date(end) : undefined;
    filterFunctions.push((element: LightweightIElement) =>
      dateFilter(element, startDate, endDate),
    );
  }

  if (filters.text) {
    filterFunctions.push((element: LightweightIElement) =>
      nameFilter(element.name, filters.text),
    );
  }

  if (filters.tags?.length) {
    filterFunctions.push((element: LightweightIElement) =>
      tagsFilter(element, filters.tags),
    );
  }
  return filterFunctions;
}

/**
 * Recursively apply filter methods to an element and all its descendants
 *
 * @param elementId ID of the element to check
 * @param elements All the elements
 * @param filters Array of filter functions to apply
 * @returns true if all filters pass for this element or its descendants
 */
function elementOrDescendantsPassFilters(
  elementId: GUID,
  elements: IElementsRecord | LightweightElementsRecord,
  filters: Array<(element: LightweightIElement) => boolean>,
): boolean {
  const element = elements[elementId];
  if (!element) return false;

  // Check if this element passes all filters
  const allFiltersPassForElement = filters.every((filter) => filter(element));
  if (allFiltersPassForElement) return true;

  // Check if all filters pass for any descendant
  if (element.childrenIds) {
    for (const childId of element.childrenIds) {
      if (elementOrDescendantsPassFilters(childId, elements, filters)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks if an element passes the creation date filter
 *
 * @param element element to check
 * @param startDate The initial date of the range
 * @param endDate The ending date of the range
 * @returns true if element passes the filter (or no filter is applied)
 */
export function dateFilter(
  element: Pick<LightweightIElement, "createdAt">,
  startDate: Date | undefined,
  endDate: Date | undefined,
): boolean {
  const elementDate = new Date(element.createdAt);

  if (startDate && elementDate < startDate) return false;
  if (endDate && elementDate > endDate) return false;

  return true;
}

/**
 * Checks if the input text contains the text filter
 *
 * @param text Element to check
 * @param textFilter Text filter to apply
 * @returns true if element name contains the filter text (or no filter is applied)
 */
export function nameFilter(text: string, textFilter: string): boolean {
  if (!textFilter) return true;

  return text.toLocaleLowerCase().includes(textFilter.toLocaleLowerCase());
}

/**
 * Checks if an element has any of the specified tags in its labels array
 *
 * @param element Element to check
 * @param tagsFilter Array of tag GUIDs to match against
 * @param isUntaggedSelected True if the UNTAGGED tag is selected
 * @returns true if element has any of the specified tags (or no filter is applied)
 */
export function tagsFilter(
  element: Pick<LightweightIElement, "labelIds">,
  tagsFilter?: GUID[],
): boolean {
  if (!tagsFilter?.length) return true;

  if (tagsFilter.includes(UNTAGGED.id) && element.labelIds?.length === 0) {
    return true;
  }

  return tagsFilter.some((tagGuid) =>
    element.labelIds?.some((labelId) => labelId === tagGuid),
  );
}

export type AreaDataSets = Record<GUID, DataSetAreaInfo[] | undefined>;

/**
 * This type encapsulates together some data that stay constant while the tree is generated
 * via the recursive functions 'generateFilteredTree' and 'generateFilteredTreeLightweight'.
 */
export type TreeGenerationData = {
  areaDataSets: AreaDataSets;
  videoModeCopyTagId: GUID | undefined;
  clusters: ClustersRecord;
};

/**
 * The fields we want to filter by. Coincides with the fields of the project filtering slice, plus {@link FilterValues.textTagIds}
 */
export type FilterValues = ProjectFilteringSlice & {
  /**
   * The IDs of the tags whose name exactly matches the search text (case-insensitive).
   * Derived from the search text and the project tags
   */
  textTagIds?: GUID[];

  /** Id of the tag identifying rooms copied for video mode datasets */
  videoModeCopyTagId?: GUID;
};

/**
 * Executing a depth-search filter on a subtree of the project, starting with the the children of the provided element.
 *
 * @param element element to start with
 * @param lightweightElements the whole lightweight elements slice, containing all the necessary metadata
 * @param filterValues values to filter by
 * @returns true iff any of the children of the element, or their descendants, is passing the filters
 */
export function filterSubtree<T extends LightweightElement>(
  element: T,
  lightweightElements: LightweightElementsRecord,
  filterValues: FilterValues,
): boolean {
  const childrenIds = lightweightElements[element.id]?.childrenIds;

  for (const childId of childrenIds ?? []) {
    const child = lightweightElements[childId];
    if (!child) return false;

    // Video mode copies are excluded when the tree is generated, so they must not
    // make an ancestor pass the filters either
    if (
      filterValues.videoModeCopyTagId &&
      child.labelIds?.includes(filterValues.videoModeCopyTagId)
    ) {
      continue;
    }

    if (filterElement(child, lightweightElements, filterValues)) {
      return true;
    }

    if (filterSubtree(child, lightweightElements, filterValues)) {
      return true;
    }
  }

  return false;
}

/**
 * @returns true iff the element is passing the filters.
 *   For some specific elements, not all filters are checked.
 *   undefined is returned for elements, that have no decisive filtering result,
 * because they either support no filters or not the current active ones.
 * @param element the element to check the filters for
 * @param lightweightElements the whole lightweight elements slice, containing all the necessary metadata
 * @param filterValues the values to filter by
 */
export function filterElement<T extends LightweightElement>(
  element: T,
  lightweightElements: LightweightElementsRecord,
  { text, dateRange = {}, tags = [], textTagIds = [] }: FilterValues,
): boolean | undefined {
  const filtersToApply = getFiltersToApply(element, lightweightElements);

  const isNameFilterActive = text;
  const isDateFilterActive = !!dateRange.start || !!dateRange.end;
  const isTagsFilterActive = !!tags.length;

  /**
   * Text filter (tag-aware).
   * An element passes the text filter if its name matches the text (when name filtering applies)
   * or if it carries a tag whose name matches the text (when tag filtering applies). These two
   * conditions are OR-combined, and stay independent from the mandatory selected-tags AND filter below.
   * When no tag matches the text (`textTagIds` is empty) this reduces to a plain name filter.
   */
  if (isNameFilterActive) {
    const canMatchByName = filtersToApply.text;
    const canMatchByTag = filtersToApply.tags && textTagIds.length > 0;

    // The text filter is not applicable to this element
    if (!canMatchByName && !canMatchByTag) return;

    const matchesByName = canMatchByName && nameFilter(element.name, text);
    const matchesByTagName = canMatchByTag && tagsFilter(element, textTagIds);

    if (!matchesByName && !matchesByTagName) {
      return false;
    }
  }

  /** Date filter */
  if (isDateFilterActive) {
    if (!filtersToApply.date) return;

    const { start, end } = dateRange;
    if (
      !dateFilter(
        element,
        start ? new Date(start) : undefined,
        end ? new Date(end) : undefined,
      )
    ) {
      return false;
    }
  }

  /** Tags filter */
  if (isTagsFilterActive) {
    if (!filtersToApply.tags) return;

    if (!tagsFilter(element, tags)) {
      return false;
    }
  }

  // Return true only if at least one filter was active (and all active filters were applicable + passed)
  return isNameFilterActive || isDateFilterActive || isTagsFilterActive
    ? true
    : undefined;
}

/** Types of filters to apply */
export type FiltersToApply = {
  /** Whether to apply the tags filter */
  tags: boolean;
  /** Whether to apply the date filter */
  date: boolean;
  /** Whether to apply the text filter */
  text: boolean;
};

// Presets to avoid allocations in hot path
const ALL_FILTERS: FiltersToApply = Object.freeze({
  tags: true,
  date: true,
  text: true,
});
const NO_FILTERS: FiltersToApply = Object.freeze({
  tags: false,
  date: false,
  text: false,
});
const ALL_EXCEPT_DATE_FILTERS: FiltersToApply = Object.freeze({
  tags: true,
  date: false,
  text: true,
});
const ONLY_TAGS_FILTER: FiltersToApply = Object.freeze({
  tags: true,
  date: false,
  text: false,
});
const ONLY_NAME_FILTER: FiltersToApply = Object.freeze({
  tags: false,
  date: false,
  text: true,
});

/**
 * @returns the filters to apply for the given element
 * @param element element to check
 * @param lightweightElements the whole lightweight elements slice, containing all the necessary metadata
 */
export function getFiltersToApply(
  element: IElement | LightweightElement,
  lightweightElements: LightweightElementsRecord,
): FiltersToApply {
  const { type, typeHint } = element;

  if (type === IElementType.section) {
    const parent = element.parentId
      ? lightweightElements[element.parentId]
      : undefined;

    // Locations should not be filtered by date, but only the time points
    const isRoomLocation =
      typeHint === IElementTypeHint.room &&
      parent?.type === IElementType.group &&
      parent.typeHint === IElementTypeHint.rooms;
    if (
      isRoomLocation ||
      typeHint === IElementTypeHint.cluster ||
      datasetTypeHints.includes(typeHint)
    ) {
      // Some project elements with children, such as clusters and generic datasets, should never
      // pass the date filter, since their capture date is generally speaking not accurate. If they
      // passed the date filter, all their children would be filtered in as well. Instead, children
      // of such elements should be filtered individually.
      return ALL_EXCEPT_DATE_FILTERS;
    }

    if (
      // isIElementRoomSection
      typeHint === IElementTypeHint.room ||
      // isIElementTimeTravelSection
      typeHint === IElementTypeHint.timeTravel ||
      // isIElementSectionDataSession
      typeHint === IElementTypeHint.dataSession ||
      // structuredE57
      typeHint === IElementTypeHint.structuredE57 ||
      // isIElementSingleScanSection
      singleScanTypeHints.includes(typeHint)
    ) {
      return ALL_FILTERS;
    }

    if (typeHint === IElementTypeHint.area) {
      // Areas should only be filtered by name
      return ONLY_NAME_FILTER;
    }
  }

  // isIElementImg360
  if (type === IElementType.img360) {
    return ALL_FILTERS;
  }

  // Is an img2d time point (i.e. it's a child of a group-attachments)
  if (type === IElementType.img2d) {
    const parent = element.parentId
      ? lightweightElements[element.parentId]
      : undefined;
    if (
      parent &&
      parent.type === IElementType.group &&
      parent.typeHint === IElementTypeHint.attachments
    ) {
      return ALL_FILTERS;
    }
    return NO_FILTERS;
  }

  // Markups: only tag filtering
  if (type === IElementType.markup) {
    return ONLY_TAGS_FILTER;
  }

  // Cluster: text and tags
  if (type === IElementType.cluster) {
    return ALL_EXCEPT_DATE_FILTERS;
  }

  return NO_FILTERS;
}

const datasetTypeHints = Object.values<string | null | undefined>(
  GENERIC_DATASET_TYPE_HINTS,
);

const singleScanTypeHints = Object.values<string | null | undefined>(
  SINGLE_SCAN_TYPE_HINTS,
);
